/**
 * Skinning + assembly.
 *
 * Parts are authored in bind-pose world space and handed to `CharBuilder.add()`
 * together with the list of bones that are ALLOWED to influence them. Weights
 * are then computed from the distance to each bone's SEGMENT (head -> tip)
 * rather than to the bone origin, which is what makes elbows, knees and the
 * waist bend instead of shear: at a joint the vertex is equidistant from both
 * segments, so it lands on a clean 50/50 blend automatically.
 *
 * Restricting the candidate set per part is the other half of the trick — a
 * pure nearest-bone search would bleed the torso onto the arms (the arms hang
 * beside the ribs in bind pose) and produce the classic melted-shoulder look.
 */
import * as THREE from 'three';
import { BONE_INDEX, createSkeleton, applyRestPose } from './rig.js';
import { tileUV, paint, indexed, mergeAll, triCount } from './geom.js';

const MAX_INFLUENCE = 4;
const _c = new THREE.Color();

function distToSegment(px, py, pz, ax, ay, az, bx, by, bz) {
  const dx = bx - ax, dy = by - ay, dz = bz - az;
  const l2 = dx * dx + dy * dy + dz * dz;
  let t = l2 > 1e-9 ? ((px - ax) * dx + (py - ay) * dy + (pz - az) * dz) / l2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = ax + dx * t, cy = ay + dy * t, cz = az + dz * t;
  return Math.hypot(px - cx, py - cy, pz - cz);
}

export class CharBuilder {
  /**
   * @param {ReturnType<import('./rig.js').makeRig>} rig
   * @param {Record<string,{u:number,v:number,w:number,h:number}>} uvRects atlas rects
   */
  constructor(rig, uvRects) {
    this.rig = rig;
    this.uv = uvRects;
    this.parts = [];
    this._tmpIdx = new Int32Array(MAX_INFLUENCE);
    this._tmpW = new Float32Array(MAX_INFLUENCE);
  }

  /**
   * @param {THREE.BufferGeometry} geo   authored in bind-pose world space
   * @param {object} o
   *   tile   atlas tile name (default 'neutral')
   *   uv     { su, sv, ou, ov, wrap, inset } uv transform inside the tile
   *   tint   THREE.Color | number — multiplies the albedo
   *   shade  fn(x,y,z) -> multiplier, for baked crease/ao darkening
   *   bones  ['chest', ['spine2', 0.7], ...]  candidate influences (+ bias)
   *   rigid  'boneName' — single influence, weight 1
   *   power  falloff exponent (default 3)
   */
  add(geo, o = {}) {
    if (!geo) return this;
    geo = indexed(geo);
    const rect = this.uv[o.tile || 'neutral'];
    if (rect) tileUV(geo, rect, o.uv || {});
    if (o.tint !== undefined) _c.set(o.tint); else _c.setRGB(1, 1, 1);
    paint(geo, _c, o.shade);
    this._weights(geo, o);
    this.parts.push(geo);
    return this;
  }

  _weights(geo, o) {
    const rig = this.rig;
    const p = geo.attributes.position;
    const n = p.count;
    const si = new Uint16Array(n * 4);
    const sw = new Float32Array(n * 4);
    if (o.rigid) {
      const bi = BONE_INDEX[o.rigid];
      if (bi === undefined) throw new Error('unknown bone ' + o.rigid);
      for (let i = 0; i < n; i++) { si[i * 4] = bi; sw[i * 4] = 1; }
      geo.setAttribute('skinIndex', new THREE.BufferAttribute(si, 4));
      geo.setAttribute('skinWeight', new THREE.BufferAttribute(sw, 4));
      return;
    }
    const list = (o.bones || ['hips']).map(b => (Array.isArray(b) ? b : [b, 1]));
    const idx = list.map(b => {
      const k = BONE_INDEX[b[0]];
      if (k === undefined) throw new Error('unknown bone ' + b[0]);
      return k;
    });
    const bias = list.map(b => b[1]);
    const pw = o.power ?? 3;
    const eps = 1e-4;
    const m = idx.length;
    const wbuf = new Float64Array(m);
    for (let i = 0; i < n; i++) {
      const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
      let sum = 0;
      for (let j = 0; j < m; j++) {
        const b = idx[j] * 3;
        const d = distToSegment(x, y, z,
          rig.world[b], rig.world[b + 1], rig.world[b + 2],
          rig.worldTip[b], rig.worldTip[b + 1], rig.worldTip[b + 2]);
        const w = bias[j] / (Math.pow(d, pw) + eps);
        wbuf[j] = w; sum += w;
      }
      // top-4 selection
      let a0 = -1, a1 = -1, a2 = -1, a3 = -1;
      let v0 = -1, v1 = -1, v2 = -1, v3 = -1;
      for (let j = 0; j < m; j++) {
        const w = wbuf[j];
        if (w > v0) { v3 = v2; a3 = a2; v2 = v1; a2 = a1; v1 = v0; a1 = a0; v0 = w; a0 = idx[j]; }
        else if (w > v1) { v3 = v2; a3 = a2; v2 = v1; a2 = a1; v1 = w; a1 = idx[j]; }
        else if (w > v2) { v3 = v2; a3 = a2; v2 = w; a2 = idx[j]; }
        else if (w > v3) { v3 = w; a3 = idx[j]; }
      }
      const tot = Math.max(1e-9, (v0 > 0 ? v0 : 0) + (v1 > 0 ? v1 : 0) + (v2 > 0 ? v2 : 0) + (v3 > 0 ? v3 : 0));
      const o4 = i * 4;
      si[o4] = a0 < 0 ? 0 : a0; sw[o4] = v0 > 0 ? v0 / tot : 1;
      si[o4 + 1] = a1 < 0 ? 0 : a1; sw[o4 + 1] = v1 > 0 ? v1 / tot : 0;
      si[o4 + 2] = a2 < 0 ? 0 : a2; sw[o4 + 2] = v2 > 0 ? v2 / tot : 0;
      si[o4 + 3] = a3 < 0 ? 0 : a3; sw[o4 + 3] = v3 > 0 ? v3 / tot : 0;
    }
    geo.setAttribute('skinIndex', new THREE.BufferAttribute(si, 4));
    geo.setAttribute('skinWeight', new THREE.BufferAttribute(sw, 4));
  }

  /** Merge every part into one buffer. */
  build() {
    if (!this.parts.length) return new THREE.BufferGeometry();
    const g = mergeAll(this.parts);
    this.parts.length = 0;
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }

  get triangles() { return this.parts.reduce((a, g) => a + triCount(g), 0); }
}

/**
 * Wrap a merged geometry into a bound SkinnedMesh.
 * The bone root is parented to the mesh (three's "attached" bind mode), so the
 * mesh can be moved/rotated freely afterwards.
 */
export function makeSkinnedMesh(geometry, rig, material, opts = {}) {
  const mesh = new THREE.SkinnedMesh(geometry, material);
  const { skeleton, bones, root, byName } = createSkeleton(rig);
  mesh.add(root);
  mesh.bind(skeleton);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.frustumCulled = opts.frustumCulled !== false;
  mesh.userData.rig = rig;
  mesh.userData.bones = byName;
  if (opts.rest !== false) applyRestPose(bones, opts.rest ?? 1, opts.phase ?? 0, opts.stance ?? 0);
  return mesh;
}
